import { useEffect } from "react";

import { useCookieConsent } from "./useCookieConsent";
import { useUserContext } from "./useUserContext";
import { createAdoptionTelemetryTokenProvider } from "../APIs/Utils/GetAdoptionTelemetryToken";
import {
  adoptionTelemetry,
  AdoptionTelemetryIdentity,
  buildAdoptionTelemetryContext
} from "../telemetry/adoptionTelemetry";
import { dmnIdentity } from "../utils/dmnIdentity";
import { getEnvironmentProps } from "../utils/environment";

const startAdoptionTelemetry = async (
  identity: AdoptionTelemetryIdentity
): Promise<void> => {
  const environmentProps = getEnvironmentProps(
    await dmnIdentity.getAccessToken()
  );
  const endpointUrl = environmentProps?.ADOPTION_TELEMETRY_URL;
  const authApiBaseUrl = environmentProps?.AUTH_API_BASE_URL;
  const context = buildAdoptionTelemetryContext(
    { ...identity, applicationId: environmentProps?.ACCESS_PRODUCT_CODE },
    crypto.randomUUID()
  );

  if (!endpointUrl || !authApiBaseUrl || !context) return;

  const tokenProvider = createAdoptionTelemetryTokenProvider(authApiBaseUrl);
  await adoptionTelemetry.start(context, {
    endpointUrl,
    getToken: tokenProvider.getToken,
    onUnauthorised: tokenProvider.invalidate
  });
};

/**
 * Starts Evo adoption telemetry (emitting the mandatory `loaded` event once
 * per app load) when analytics consent is granted and the user's Access
 * identity and organisation are known.
 */
export const useAdoptionTelemetry = () => {
  const { user } = useUserContext();
  const { consent } = useCookieConsent();

  const userId = user?.workspace_id;
  const organisationId = user?.workspace_organisation_id;

  useEffect(() => {
    if (!consent.isResolved || !consent.analytics) return;
    if (!userId || !organisationId) return;

    startAdoptionTelemetry({ userId, organisationId }).catch(() => undefined);
  }, [consent.isResolved, consent.analytics, userId, organisationId]);
};
